import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-loading-skeleton',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="loading-skeleton-container">
      <!-- Messages Tab Skeleton -->
      <ng-container *ngIf="type === 'Messages'">
        <div class="loading-header">
          <div class="skeleton-row">
            <div class="skeleton-item header-item"></div>
          </div>
        </div>
        
        <div class="skeleton-content">
          <div *ngFor="let item of itemsArray" class="skeleton-row message-row">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-message">
              <div class="skeleton-item sender"></div>
              <div class="skeleton-item message-content"></div>
              <div class="skeleton-item timestamp"></div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Notes Tab Skeleton -->
      <ng-container *ngIf="type === 'Note'">
        <div class="loading-header">
          <div class="skeleton-row">
            <div class="skeleton-item header-item"></div>
          </div>
        </div>
        
        <div class="skeleton-content">
          <div *ngFor="let item of itemsArray" class="skeleton-row note-row">
            <div class="skeleton-item note-timestamp"></div>
            <div class="skeleton-item note-content"></div>
          </div>
        </div>
      </ng-container>

      <!-- Activity Tab Skeleton -->
      <ng-container *ngIf="type === 'Activity'">
        <div class="loading-header">
          <div class="skeleton-row">
            <div class="skeleton-item activity-input"></div>
          </div>
        </div>
        
        <div class="skeleton-content">
          <div *ngFor="let item of itemsArray" class="skeleton-row activity-row">
            <div class="skeleton-icon"></div>
            <div class="skeleton-activity-details">
              <div class="skeleton-item activity-type"></div>
              <div class="skeleton-item activity-time"></div>
              <div class="skeleton-item activity-person"></div>
            </div>
            <div class="skeleton-item activity-menu"></div>
          </div>
        </div>
      </ng-container>

      <!-- Documents Tab Skeleton -->
      <ng-container *ngIf="type === 'Documents'">
        <div class="loading-header">
          <div class="skeleton-row">
            <div class="skeleton-item header-item"></div>
            <div class="skeleton-button document-button"></div>
          </div>
        </div>
        
        <div class="skeleton-content">
          <div *ngFor="let item of itemsArray" class="skeleton-row document-row">
            <div class="skeleton-item document-name"></div>
            <div class="skeleton-item document-type"></div>
            <div class="skeleton-item document-status"></div>
          </div>
        </div>
      </ng-container>

      <!-- Changelog Tab Skeleton -->
      <ng-container *ngIf="type === 'Changelog'">
        <div class="skeleton-content">
          <div *ngFor="let item of itemsArray" class="skeleton-row changelog-row">
            <div class="skeleton-timeline-dot"></div>
            <div class="skeleton-changelog-details">
              <div class="skeleton-item changelog-field"></div>
              <div class="skeleton-item changelog-values"></div>
              <div class="skeleton-item changelog-timestamp"></div>
              <div class="skeleton-item changelog-user"></div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- Default Skeleton -->
      <ng-container *ngIf="!type || (type !== 'Messages' && type !== 'Note' && type !== 'Activity' && type !== 'Documents' && type !== 'Changelog')">
        <div class="loading-header">
          <div class="skeleton-row">
            <div class="skeleton-item header-item"></div>
          </div>
        </div>
        
        <div class="skeleton-content">
          <div *ngFor="let item of itemsArray" class="skeleton-row">
            <div class="skeleton-item"></div>
            <div class="skeleton-item"></div>
          </div>
        </div>
        
        <div class="loading-actions">
          <div class="skeleton-row">
            <div class="skeleton-button"></div>
            <div class="skeleton-button"></div>
          </div>
        </div>
      </ng-container>
    </div>
  `,
  styles: [`
    .loading-skeleton-container {
      padding: 1rem;
      width: 100%;
    }

    .loading-header {
      margin-bottom: 1.5rem;
    }

    .skeleton-row {
      display: flex;
      margin-bottom: 0.75rem;
      align-items: center;
      gap: 1rem;
    }

    .skeleton-item {
      height: 1rem;
      background: linear-gradient(90deg, var(--skeleton-shimmer-from) 25%, var(--skeleton-shimmer-mid) 50%, var(--skeleton-shimmer-to) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 4px;
      width: 100%;
    }

    .header-item {
      height: 1.5rem;
      width: 40%;
    }

    .skeleton-content {
      margin-bottom: 1.5rem;
    }

    .loading-actions {
      display: flex;
      justify-content: flex-end;
    }

    .skeleton-button {
      height: 2.25rem;
      width: 6rem;
      border-radius: 4px;
      background: linear-gradient(90deg, var(--skeleton-shimmer-from) 25%, var(--skeleton-shimmer-mid) 50%, var(--skeleton-shimmer-to) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    /* Messages Tab Styles */
    .skeleton-avatar {
      height: 2.5rem;
      width: 2.5rem;
      border-radius: 50%;
      background: linear-gradient(90deg, var(--skeleton-shimmer-from) 25%, var(--skeleton-shimmer-mid) 50%, var(--skeleton-shimmer-to) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      flex-shrink: 0;
    }

    .skeleton-message {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
    }

    .sender {
      width: 30%;
      height: 1rem;
    }

    .message-content {
      width: 90%;
      height: 1rem;
    }

    .timestamp {
      width: 20%;
      height: 0.8rem;
      align-self: flex-end;
    }

    /* Note Tab Styles */
    .note-row {
      flex-direction: column;
      align-items: flex-start;
      padding: 1rem;
      border-radius: 8px;
      background-color: var(--skeleton-row-even-bg);
      margin-bottom: 1rem;
    }

    .note-timestamp {
      width: 20%;
      height: 0.8rem;
      margin-bottom: 0.5rem;
    }

    .note-content {
      width: 100%;
      height: 1.2rem;
    }

    /* Activity Tab Styles */
    .activity-input {
      height: 3rem;
      border-radius: 8px;
    }

    .skeleton-icon {
      height: 2rem;
      width: 2rem;
      border-radius: 50%;
      background: linear-gradient(90deg, var(--skeleton-shimmer-from) 25%, var(--skeleton-shimmer-mid) 50%, var(--skeleton-shimmer-to) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      flex-shrink: 0;
    }

    .skeleton-activity-details {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      flex-grow: 1;
    }

    .activity-type {
      width: 30%;
      height: 1rem;
    }

    .activity-time {
      width: 20%;
      height: 0.8rem;
    }

    .activity-person {
      width: 40%;
      height: 0.8rem;
    }

    .activity-menu {
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
    }

    /* Documents Tab Styles */
    .document-button {
      margin-left: auto;
    }

    .document-row {
      padding: 0.75rem;
      border-radius: 4px;
      background-color: var(--skeleton-row-even-bg);
      margin-bottom: 1rem;
    }

    .document-name {
      width: 40%;
    }

    .document-type {
      width: 20%;
    }

    .document-status {
      width: 15%;
      margin-left: auto;
    }

    /* Changelog Tab Styles */
    .changelog-row {
      position: relative;
      margin-left: 1.5rem;
    }

    .skeleton-timeline-dot {
      position: absolute;
      left: -1.5rem;
      height: 1rem;
      width: 1rem;
      border-radius: 50%;
      background: linear-gradient(90deg, var(--skeleton-shimmer-from) 25%, var(--skeleton-shimmer-mid) 50%, var(--skeleton-shimmer-to) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    .skeleton-changelog-details {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
      border-left: 2px solid var(--skeleton-border-color);
      padding-left: 1rem;
    }

    .changelog-field {
      width: 30%;
      height: 1rem;
    }

    .changelog-values {
      width: 50%;
      height: 1rem;
    }

    .changelog-timestamp, .changelog-user {
      width: 25%;
      height: 0.8rem;
    }
    
    @keyframes shimmer {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `]
})
export class LoadingSkeletonComponent {
  @Input() rows: number = 5;
  @Input() type: string = '';
  
  get itemsArray(): number[] {
    return Array(this.rows).fill(0);
  }
}